import i18n from "@/i18n";

/**
 * Paket-bazlı erişim kontrolü.
 *
 * Her paket bir programa karşılık gelir:
 *   STARTER → Mobility & Control
 *   PREMIUM → Strength in Range
 *   ELITE   → Functional Resilience
 *
 * Bir kullanıcı sadece SATIN ALDIĞI paketin programına ve flow videolarına
 * erişebilir. Diyet, AI, randevu ve öncelikli destek hakları ortaktır —
 * herhangi bir aktif abonelik (STARTER / PREMIUM / ELITE) yeterlidir.
 * Paketler arası fark yalnızca program (içerik) erişimindedir.
 */

export type PackageTier = "STARTER" | "PREMIUM" | "ELITE";

export type Feature =
  | "training_basic"          // Ücretsiz programlar (herkese açık)
  | "videos_free"             // Ücretsiz flow videolar
  | "diet_basic"              // Ücretsiz diyet
  | "diet_premium"            // Premium diyet (herhangi paket)
  | "appointments"            // Randevu (herhangi paket)
  | "appointments_unlimited"  // Sınırsız randevu (herhangi paket)
  | "ai_chat"                 // AI sohbet (herhangi paket)
  | "ai_analysis"             // AI ilerleme analizi (herhangi paket)
  | "priority_support"        // Öncelikli destek (herhangi paket)
  // Geriye uyumluluk — eski kodlar için "premium içerik genel kontrolü"
  // (herhangi bir paket aktifse açılır). Yeni kodlar program-spesifik
  // hasProgramAccess() helper'ını kullanmalı.
  | "training_premium"
  | "videos_all";

/** Tüm ücretli paketlerde ortak özellik seti. */
const SHARED_PACKAGE_FEATURES: Feature[] = [
  "diet_premium",
  "appointments",
  "appointments_unlimited",
  "ai_chat",
  "ai_analysis",
  "priority_support",
];

const PACKAGE_FEATURES: Record<PackageTier, Feature[]> = {
  STARTER: [...SHARED_PACKAGE_FEATURES],
  PREMIUM: [...SHARED_PACKAGE_FEATURES],
  ELITE:   [...SHARED_PACKAGE_FEATURES],
};

const FREE_FEATURES: Feature[] = ["training_basic", "videos_free", "diet_basic"];

export const normalizeTier = (raw: string | null | undefined): PackageTier | null => {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === "STARTER" || upper === "PREMIUM" || upper === "ELITE") return upper as PackageTier;
  return null;
};

/** Aktif paket tier listesinden özellik erişimini hesapla. */
export const hasFeatureAccess = (
  activeTiers: ReadonlyArray<string | null | undefined> | string | null | undefined,
  feature: Feature,
): boolean => {
  if (FREE_FEATURES.includes(feature)) {
    // training_basic / videos_free / diet_basic geriye uyumluluk:
    // tier yoksa bile (misafir üye / GUEST_MEMBER) free içeriklere erişir.
    return true;
  }
  const tiers = Array.isArray(activeTiers)
    ? activeTiers
    : activeTiers
      ? [activeTiers]
      : [];
  for (const t of tiers) {
    const norm = normalizeTier(t ?? undefined);
    if (norm && PACKAGE_FEATURES[norm].includes(feature)) return true;
  }
  return false;
};

/** Programa özel erişim kontrolü. */
export const hasProgramAccess = (
  activeProgramIds: ReadonlyArray<string | null | undefined>,
  programId: string | null | undefined,
): boolean => {
  if (!programId) return false;
  return activeProgramIds.some((p) => p === programId);
};

/** Geri uyumluluk: tek "planName" alıp herhangi bir feature erişimi. */
export const hasFeatureAccessLegacy = (planName: string | null | undefined, feature: Feature) =>
  hasFeatureAccess(planName ?? null, feature);

export const PACKAGE_LABELS: Record<PackageTier, string> = {
  STARTER: "Mobility & Control",
  PREMIUM: "Strength in Range",
  ELITE:   "Functional Resilience",
};

const KNOWN_LABELS = new Set<string>(Object.values(PACKAGE_LABELS));

/** Slug-benzeri bir değer mi? ("vip_plus", "GOLD", "tier-2", boş vs.) */
const looksLikeRawSlug = (value: string): boolean => {
  const v = value.trim();
  if (!v) return true;
  if (KNOWN_LABELS.has(v)) return false;
  // Tek kelime + (tamamı büyük harf VEYA tamamı küçük harf VEYA snake/kebab) → slug say
  if (!/\s/.test(v) && (/^[A-Z0-9_-]+$/.test(v) || /^[a-z0-9_-]+$/.test(v))) return true;
  return false;
};

/**
 * UI helper: bir abonelikteki tier slug'ını ("STARTER" / "PREMIUM" / "ELITE")
 * paket adına çevirir ("Mobility & Control" vb.).
 *
 * - Bilinen tier → resmi paket adı
 * - Zaten paket adı taşıyan eski kayıtlar (ör. "Strength in Range") → olduğu gibi
 * - Tanınmayan slug-benzeri değerler ("vip_plus", "GOLD" vb.) → fallback metin
 *   (varsayılan: i18n'deki `common.membershipFallback`, yoksa "Üyelik Paketi")
 */
export const getPackageLabel = (
  raw: string | null | undefined,
  fallback?: string,
): string => {
  const resolveFallback = (): string => {
    if (fallback !== undefined) return fallback;
    try {
      const translated = i18n?.t?.("common.membershipFallback", {
        defaultValue: "Üyelik Paketi",
      });
      if (typeof translated === "string" && translated.trim()) return translated;
    } catch {
      /* i18n hazır değil — sessizce geç */
    }
    return "Üyelik Paketi";
  };

  if (!raw) return resolveFallback();
  const tier = normalizeTier(raw);
  if (tier) return PACKAGE_LABELS[tier];
  if (looksLikeRawSlug(raw)) return resolveFallback();
  return raw;
};

/** Bir özelliğe erişmek için hangi paket(ler) gerekiyor? UI mesajları için. */
export const getRequiredPackages = (feature: Feature): PackageTier[] => {
  if (FREE_FEATURES.includes(feature)) return [];
  return (Object.keys(PACKAGE_FEATURES) as PackageTier[]).filter((t) =>
    PACKAGE_FEATURES[t].includes(feature),
  );
};

// ---------------------------------------------------------------------------
// Geri uyumluluk shim'leri — eski 2-tier API'sini koruyor.
// ---------------------------------------------------------------------------

export type PlanTier = PackageTier;
export const PLAN_LABELS: Record<PackageTier, string> = PACKAGE_LABELS;

/** Legacy: bir feature için "en hafif" paket adını döner (UI için). */
export const getRequiredPlan = (feature: Feature): PackageTier => {
  const required = getRequiredPackages(feature);
  return required[0] ?? "STARTER";
};

// ---------------------------------------------------------------------------
// Tier hiyerarşisi: STARTER < PREMIUM < ELITE
// ---------------------------------------------------------------------------
export const TIER_RANK: Record<PackageTier, number> = {
  STARTER: 1,
  PREMIUM: 2,
  ELITE:   3,
};

export const tierRank = (tier: string | null | undefined): number => {
  const norm = normalizeTier(tier ?? undefined);
  return norm ? TIER_RANK[norm] : 0;
};

/** Aktif tier listesinden en yüksek rank'i döner (0 = hiç paket yok). */
export const maxTierRank = (activeTiers: ReadonlyArray<string | null | undefined>): number => {
  let max = 0;
  for (const t of activeTiers) {
    const r = tierRank(t);
    if (r > max) max = r;
  }
  return max;
};

/** Kullanıcının aktif tier'ları, içerik için gerekli minimum tier'ı karşılıyor mu? */
export const hasMinTier = (
  activeTiers: ReadonlyArray<string | null | undefined>,
  minTier: string | null | undefined,
): boolean => {
  const required = tierRank(minTier);
  if (required === 0) return true; // min_tier yoksa kısıt yok
  return maxTierRank(activeTiers) >= required;
};


